import { useSyncExternalStore } from 'react'

/**
 * Bridge store between the Workbench chart and the AI chat (both live under
 * BotDetail but in separate subtrees).
 *
 * The chart context is a STICKY READ FLAG, not a manual attachment: while
 * `on`, the Workbench keeps the latest chart context staged (rebuilt on every
 * chart edit, incrementally diffed against what the AI last saw) and every
 * message the user sends carries it. There is no attach step and no badge.
 *
 * Staging has to happen Workbench-side rather than at send time because the
 * Workbench — and with it the chart engine — unmounts on the Backtest and
 * Optimise tabs. The last staged context survives there, so a send from any
 * tab still carries the chart.
 */

/** Minimum time the refresh spinner stays up, so a fast rebuild doesn't flicker. */
const MIN_BUSY_MS = 500

export interface StagedChart {
  label: string
  text: string
  /** Context items keyed by stable id — becomes aiPush.sentItems on commit. */
  items: Record<string, string>
  /** The symbol the context was built from — chart state and AI bookkeeping
   * are per-symbol, so the commit must fold into THAT symbol's diff base. */
  symbol: string
  /** When this context was built (ms) — the row renders it as relative time. */
  atMs: number
}

export interface ChartPushState {
  bid: string | null
  /** Sticky: while true, the chart rides along with every message sent. */
  on: boolean
  /** A chart edit landed and the rebuild hasn't settled yet (the spinner). */
  busy: boolean
  /** When the AI's view of the chart last changed (ms) — staged or sent. */
  updatedAt: number | null
  /** Context the next message will carry; null once sent or when nothing differs. */
  staged: StagedChart | null
  /** Commits this session (>0 ⇒ "has pushed"); seeded from persisted state. */
  pushCount: number
  /** Last commit — lets the Workbench reconcile a send that happened while it was unmounted. */
  lastCommit: { sentAt: number; sentItems: Record<string, string>; symbol: string } | null
  /** True once the Workbench seeded from the persisted workbench state. */
  seeded: boolean
}

/**
 * `on` and `updatedAt` persist in localStorage rather than the workbench blob:
 * the blob is written by the Workbench's autosave, so a user who unticks the
 * box from the Backtest tab (Workbench unmounted) would never have it saved.
 * Same class of preference as the effort selector — see chatEffort.ts.
 */
const KEY_PREFIX = 'algobuilder.chartContext.'

interface PersistedPrefs {
  on: boolean
  updatedAt: number | null
}

const DEFAULT_PREFS: PersistedPrefs = { on: true, updatedAt: null }

function loadPrefs(bid: string): PersistedPrefs {
  try {
    const raw = localStorage.getItem(KEY_PREFIX + bid)
    if (!raw) return DEFAULT_PREFS
    const parsed: unknown = JSON.parse(raw)
    if (typeof parsed !== 'object' || parsed === null) return DEFAULT_PREFS
    const rec = parsed as Record<string, unknown>
    return {
      on: typeof rec.on === 'boolean' ? rec.on : DEFAULT_PREFS.on,
      updatedAt:
        typeof rec.updatedAt === 'number' && Number.isFinite(rec.updatedAt) ? rec.updatedAt : null,
    }
  } catch {
    return DEFAULT_PREFS
  }
}

function savePrefs(bid: string | null, prefs: PersistedPrefs) {
  if (!bid) return
  try {
    localStorage.setItem(KEY_PREFIX + bid, JSON.stringify(prefs))
  } catch {
    // storage unavailable — the setting just won't survive a refresh
  }
}

const fresh = (bid: string | null): ChartPushState => {
  const prefs = bid ? loadPrefs(bid) : DEFAULT_PREFS
  return {
    bid,
    on: prefs.on,
    busy: false,
    updatedAt: prefs.updatedAt,
    staged: null,
    pushCount: 0,
    lastCommit: null,
    seeded: false,
  }
}

let state: ChartPushState = fresh(null)
const listeners = new Set<(s: ChartPushState) => void>()
// Stable per-bid empty snapshot, so the hook's getSnapshot stays pure and
// referentially stable while the store still holds another bot's state.
let freshSnap: ChartPushState = fresh(null)
let busySince = 0
let busyTimer: ReturnType<typeof setTimeout> | null = null

function emit() {
  listeners.forEach((fn) => fn(state))
}

/** Reset to a clean slate when a different bot's workbench/chat touches the store. */
function ensure(bid: string) {
  if (state.bid !== bid) {
    if (busyTimer) {
      clearTimeout(busyTimer)
      busyTimer = null
    }
    state = fresh(bid)
    emit()
  }
}

/** Pure read for render paths: never mutates, never emits. */
function snapshotFor(bid: string): ChartPushState {
  if (state.bid === bid) return state
  if (freshSnap.bid !== bid) freshSnap = fresh(bid)
  return freshSnap
}

/** Drop `busy`, but never sooner than MIN_BUSY_MS after it went up. */
function settleBusy() {
  if (!state.busy) return
  const elapsed = Date.now() - busySince
  if (elapsed >= MIN_BUSY_MS) {
    state = { ...state, busy: false }
    return
  }
  if (busyTimer) return
  busyTimer = setTimeout(() => {
    busyTimer = null
    if (!state.busy) return
    state = { ...state, busy: false }
    emit()
  }, MIN_BUSY_MS - elapsed)
}

export const chartPush = {
  /** Read for event handlers — adopts the bid (resets if it changed). */
  get(bid: string): ChartPushState {
    ensure(bid)
    return state
  },

  ensure,

  subscribe(fn: (s: ChartPushState) => void): () => void {
    listeners.add(fn)
    return () => listeners.delete(fn)
  },

  /** The chart-context tick box. Turning it off drops whatever was staged. */
  setOn(bid: string, on: boolean) {
    ensure(bid)
    if (state.on === on) return
    state = { ...state, on, ...(on ? {} : { staged: null, busy: false }) }
    savePrefs(bid, { on, updatedAt: state.updatedAt })
    emit()
  },

  /** A chart edit landed — spin the refresh glyph until stage()/clearStaged(). */
  touch(bid: string) {
    ensure(bid)
    if (!state.on || state.busy) return
    busySince = Date.now()
    state = { ...state, busy: true }
    emit()
  },

  /** Seed the staged chip and push count from the persisted workbench state,
   * so a reload keeps sending the context the user last had on the chart. */
  seedIfFresh(bid: string, seed: { hasPushes: boolean; staged?: StagedChart | null }) {
    ensure(bid)
    if (state.seeded || state.staged || state.pushCount > 0) return
    state = {
      ...state,
      staged: state.on ? (seed.staged ?? null) : null,
      pushCount: seed.hasPushes ? 1 : 0,
      seeded: true,
    }
    emit()
  },

  /** Put the freshly built chart context on the composer, replacing any prior. */
  stage(
    bid: string,
    input: { label: string; text: string; items: Record<string, string>; symbol: string; atMs: number },
  ): StagedChart {
    ensure(bid)
    const staged: StagedChart = { ...input }
    state = { ...state, staged, updatedAt: input.atMs }
    settleBusy()
    savePrefs(bid, { on: state.on, updatedAt: state.updatedAt })
    emit()
    return staged
  },

  /** Nothing left to send (context matches what the AI already has, or the
   * box was unticked) — drop the staged payload without touching updatedAt. */
  clearStaged(bid: string) {
    ensure(bid)
    if (!state.staged && !state.busy) return
    state = { ...state, staged: null }
    settleBusy()
    emit()
  },

  /** The message carrying the staged context was sent successfully. */
  commit(bid: string): StagedChart | null {
    ensure(bid)
    const staged = state.staged
    if (!staged) return null
    const sentAt = Date.now()
    state = {
      ...state,
      staged: null,
      updatedAt: sentAt,
      pushCount: state.pushCount + 1,
      lastCommit: { sentAt, sentItems: staged.items, symbol: staged.symbol },
    }
    savePrefs(bid, { on: state.on, updatedAt: sentAt })
    emit()
    return staged
  },

  reset(bid: string) {
    if (busyTimer) {
      clearTimeout(busyTimer)
      busyTimer = null
    }
    state = fresh(bid)
    emit()
  },
}

export function useChartPush(bid: string): ChartPushState {
  return useSyncExternalStore(chartPush.subscribe, () => snapshotFor(bid))
}
